import type { Dispatch, PropsWithChildren, ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { partition } from 'lodash-es'
import { useLocation } from 'react-router'
import { useSelector } from 'react-redux'

import {
  CATEGORY_PAGES,
  SET_CATEGORY_STATE,
  SET_ERROR_STATE,
  SET_IS_EDITING,
  SET_IS_VIEW_MODE,
  SET_LINK_DETAILS,
  dropdownCategoryInitialState,
  errorInitialState,
  initialState,
} from './LinkTooltip.constants'
import type { CategoryState, ItemType, ItemsArray, LinkTooltipErrorState, LinkTooltipState } from './LinkTooltip.utils'
import { ToolbarContext } from '../../text/RichTextEditor'
import {
  createLinkBuilder,
  filterMultipleItems,
  flattenAndExtractProperties,
  normalizeMultipleItems,
} from './LinkTooltip.utils'
import { useProductSuggestedSearch } from '../../../../../../utils/hooks/useProductSuggestedSearch'
import { useTranslate } from '../../../../../../utils/translate'

export type CategoryActionType = { type: typeof SET_CATEGORY_STATE; payload: CategoryState }
export type ErrorActionType = { type: typeof SET_ERROR_STATE; payload: LinkTooltipErrorState }
export type LinkTooltipActionTypes =
  | { type: typeof SET_IS_VIEW_MODE; payload: boolean }
  | { type: typeof SET_LINK_DETAILS; payload: Partial<LinkTooltipState> }
  | { type: typeof SET_IS_EDITING; payload: boolean }

export const dropdownCategoryReducer = (state: CategoryState, action: CategoryActionType): CategoryState => {
  if (action.type === SET_CATEGORY_STATE) {
    return { ...state, ...action.payload }
  }

  return state
}

export const linkTooltipReducer = (state: LinkTooltipState, action: LinkTooltipActionTypes): LinkTooltipState => {
  switch (action.type) {
    case SET_IS_VIEW_MODE:
      return { ...state, isViewMode: action.payload }
    case SET_LINK_DETAILS:
      return { ...state, ...action.payload }
    case SET_IS_EDITING:
      return { ...state, isEditing: action.payload }
    default:
      return state
  }
}

export const errorReducer = (state: LinkTooltipErrorState, action: ErrorActionType): LinkTooltipErrorState => {
  if (action.type === SET_ERROR_STATE) {
    return { ...state, hasError: action.payload.hasError }
  }
  return state
}

export interface TooltipStateContextProps {
  linkTooltipState: LinkTooltipState
  linkTooltipDispatch: Dispatch<LinkTooltipActionTypes>
  categoryState: CategoryState
  categoryDispatch: Dispatch<CategoryActionType>
  errorState: LinkTooltipErrorState
  errorDispatch: Dispatch<ErrorActionType>
  pages: ItemType[]
  products: ItemType[]
  t: TFunction
}

const TooltipStateContext = createContext<TooltipStateContextProps | undefined>(undefined)

export default function TooltipStateProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [{ isLinkTooltipOpen, isButtonLinkTooltipOpen }] = useContext(ToolbarContext)
  const [linkTooltipState, linkTooltipDispatch] = useReducer(linkTooltipReducer, initialState)
  const [categoryState, categoryDispatch] = useReducer(dropdownCategoryReducer, dropdownCategoryInitialState)
  const [errorState, errorDispatch] = useReducer(errorReducer, errorInitialState)
  const t = useTranslate('shop') as TFunction

  const contextValue = useMemo(
    () => ({
      linkTooltipState: initialState,
      linkTooltipDispatch,
      categoryState: dropdownCategoryInitialState,
      categoryDispatch,
      errorState: errorInitialState,
      errorDispatch,
      pages: [],
      products: [],
      t,
    }),
    [linkTooltipDispatch, categoryDispatch, errorDispatch, t],
  )

  useEffect(
    function resetStateWhenTooltipCloses() {
      if (!isLinkTooltipOpen && !isButtonLinkTooltipOpen) {
        categoryDispatch({ type: SET_CATEGORY_STATE, payload: dropdownCategoryInitialState })
        linkTooltipDispatch({
          type: SET_LINK_DETAILS,
          payload: initialState,
        })

        if (errorState.hasError) {
          errorDispatch({ type: SET_ERROR_STATE, payload: { hasError: false } })
        }
      }
    },
    [errorState.hasError, isButtonLinkTooltipOpen, isLinkTooltipOpen],
  )

  return isLinkTooltipOpen || isButtonLinkTooltipOpen ? (
    <OpenedTooltipStateProvider
      {...{ t, linkTooltipState, linkTooltipDispatch, categoryState, categoryDispatch, errorState, errorDispatch }}
    >
      {children}
    </OpenedTooltipStateProvider>
  ) : (
    <TooltipStateContext.Provider value={contextValue}>{children}</TooltipStateContext.Provider>
  )
}

export function OpenedTooltipStateProvider({
  children,
  linkTooltipState,
  linkTooltipDispatch,
  categoryState,
  categoryDispatch,
  errorState,
  errorDispatch,
  t,
}: Readonly<Omit<PropsWithChildren<TooltipStateContextProps>, 'products' | 'pages'>>) {
  const pathname = useLocation().pathname.replace(/^\/editor/, '')
  const linkBuilder = useMemo(() => createLinkBuilder(pathname), [pathname])

  const shouldFetchProducts = linkTooltipState.isEditing && categoryState.selectedCategory !== CATEGORY_PAGES

  const searchResultsProducts = useProductSuggestedSearch(linkTooltipState.searchTerm, shouldFetchProducts)

  const { contentPagesState, footerPagesState, legalPagesState } = useSelector((state: State) => ({
    contentPagesState: state.getIn(['navigation', 'storefront']),
    footerPagesState: state.get('footerPages'),
    legalPagesState: state.get('legalPages'),
  }))

  const [categoryItems, contentItems, footerItems, legalItems] = useMemo(() => {
    const [categoryPagesState, nonCategoryContentPages] = partition(
      contentPagesState?.toJS(),
      (page: { type: string }) => page.type === 'category',
    )

    const propertiesPages = ['id', 'title', 'slug', 'href']
    const categoryPages = flattenAndExtractProperties(categoryPagesState, propertiesPages)
    const contentPages = flattenAndExtractProperties(nonCategoryContentPages, propertiesPages)
    const legalPages = flattenAndExtractProperties(legalPagesState?.toJS(), propertiesPages, (title) => t(title))
    const footerPages = flattenAndExtractProperties(footerPagesState?.toJS(), propertiesPages)

    const staticItemsToNormalize: ItemsArray[] = [
      { items: categoryPages, id: 'id', titleKey: 'title', type: 'category' },
      { items: contentPages, id: 'id', titleKey: 'title', type: 'content' },
      { items: footerPages, id: 'id', titleKey: 'title', type: 'footer' },
      { items: legalPages, id: 'id', titleKey: 'title', type: 'legal' },
    ]
    return normalizeMultipleItems(staticItemsToNormalize, linkBuilder)
  }, [contentPagesState, legalPagesState, footerPagesState, linkBuilder, t])

  const productItems = useMemo(() => {
    return normalizeMultipleItems(
      [{ items: searchResultsProducts, id: 'productId', titleKey: 'name', type: 'product' }],
      linkBuilder,
    )[0]
  }, [searchResultsProducts, linkBuilder])

  const [filteredCategoryItems, filteredContentItems, filteredFooterItems, filteredLegalItems] = filterMultipleItems(
    [categoryItems, contentItems, footerItems, legalItems],
    linkTooltipState.searchTerm,
  )

  const filteredPages = useMemo(
    () => [...filteredCategoryItems, ...filteredContentItems, ...filteredFooterItems, ...filteredLegalItems],
    [filteredCategoryItems, filteredContentItems, filteredFooterItems, filteredLegalItems],
  )

  const contextValue = useMemo(
    () => ({
      linkTooltipState,
      linkTooltipDispatch,
      categoryState,
      categoryDispatch,
      errorState,
      errorDispatch,
      pages: filteredPages,
      products: productItems,
      t,
    }),
    [
      linkTooltipState,
      linkTooltipDispatch,
      categoryState,
      categoryDispatch,
      errorState,
      errorDispatch,
      filteredPages,
      productItems,
      t,
    ],
  )

  return <TooltipStateContext.Provider value={contextValue}>{children}</TooltipStateContext.Provider>
}

export const useTooltipState = () => {
  const context = useContext(TooltipStateContext)
  if (!context) {
    throw new Error('useTooltipState must be used within a TooltipStateProvider')
  }
  return context
}
